/** A request's thread: who said what and when, each entry, and the box to comment. */

import { useState } from 'react';
import { ArrowRight, ListChecks, Send } from 'lucide-react';

import { CommentIn, commentInBodyMax } from '@/api/generated/schemas';
import { useCommentFeedback } from '@/api/queries';
import type { FeedbackActivity, FeedbackDetail, PersonRef } from '@/api/types';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { Markdown } from '@/ui/Markdown';
import { MarkdownEditor } from '@/ui/MarkdownEditor';
import { FormError } from '@/ui/StateCard';
import { formatDateTime, initialsOf } from '@/ui/format';

import { AttachmentList, AttachmentPicker, useAttachmentDraft } from './Attachments';
import { StatusPill, isOpen, relativeTime } from './feedbackUi';

export function When({ iso, inline = false }: { iso: string; inline?: boolean }) {
  return (
    <time dateTime={iso} title={formatDateTime(iso) ?? undefined}>
      {relativeTime(iso, { inline })}
    </time>
  );
}

export function Who({ person, requester }: { person: PersonRef; requester: boolean }) {
  return (
    <>
      <b>{person.display_name}</b>
      <span className={`badge ${requester ? 'badge-neutral' : 'badge-accent'} thread-role`}>{requester ? 'Requester' : 'Admin'}</span>
    </>
  );
}

export function ThreadEntry({ entry }: { entry: FeedbackActivity }) {
  if (entry.kind === 'status' && entry.to_status) {
    const verb = entry.to_status === 'cancelled' ? 'cancelled the request' : 'changed the status';
    return (
      <li className="thread-event">
        <span className="thread-event-icon" aria-hidden="true">
          <ListChecks />
        </span>
        <div className="thread-event-body">
          <div className="thread-event-line">
            <Who person={entry.author} requester={entry.by_requester} /> {verb}
            {entry.to_status !== 'cancelled' && (
              <>
                {entry.from_status && <StatusPill status={entry.from_status} />}
                <ArrowRight aria-hidden="true" className="thread-arrow" />
                <StatusPill status={entry.to_status} />
              </>
            )}
            <span className="hint">
              · <When iso={entry.created_at} inline />
            </span>
          </div>
          {entry.body && (
            <div className="thread-note">
              <Markdown>{entry.body}</Markdown>
            </div>
          )}
        </div>
      </li>
    );
  }
  return (
    <li className="thread-comment">
      <span className={`avatar sm${entry.by_requester ? ' muted' : ''}`} aria-hidden="true">
        {initialsOf(entry.author.display_name)}
      </span>
      <div className="thread-bubble">
        <div className="thread-meta">
          <Who person={entry.author} requester={entry.by_requester} />
          <span className="hint">
            · <When iso={entry.created_at} inline />
          </span>
        </div>
        <Markdown>{entry.body ?? ''}</Markdown>
        <AttachmentList attachments={entry.attachments} />
      </div>
    </li>
  );
}

export function CommentBox({ item }: { item: FeedbackDetail }) {
  const comment = useCommentFeedback();
  const [body, setBody] = useState('');
  const files = useAttachmentDraft();
  const invalid = Object.keys(fieldErrors(CommentIn.pick({ body: true }), { body })).length > 0;
  const server = serverFieldErrors(comment.error);

  if (!item.permissions.can_comment) {
    return <p className="hint thread-closed">This request was cancelled, so it takes no more comments.</p>;
  }
  return (
    <form
      className="thread-reply"
      onPaste={files.onPaste}
      onSubmit={(e) => {
        e.preventDefault();
        comment.mutate(
          { id: item.id, body: body.trim(), attachment_ids: files.ids },
          {
            onSuccess: () => {
              setBody('');
              files.sent();
            },
          },
        );
      }}
    >
      <FormError error={bannerError(comment.error, ['body'])} />
      <label htmlFor="feedback-comment" className="sr-only">
        Add a comment
      </label>
      <MarkdownEditor
        id="feedback-comment"
        value={body}
        onChange={setBody}
        maxLength={commentInBodyMax}
        rows={4}
        placeholder={
          isOpen(item.status) ? 'Add a comment' : 'Add a comment. Say so here if it is not resolved for you; an admin can reopen it.'
        }
        error={server.body}
      />
      <div className="thread-reply-actions">
        <AttachmentPicker draft={files} id="feedback-comment-files" compact />
        <ActionButton
          state={buttonState(comment)}
          label="Comment"
          pendingLabel="Sending…"
          doneLabel="Sent"
          icon={<Send aria-hidden="true" />}
          disabled={invalid || files.busy}
        />
      </div>
    </form>
  );
}
